import differenceInDays from 'date-fns/differenceInDays';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import format from 'date-fns/format';
import uniq from 'lodash/uniq';
import { getFormattedValue } from 'src/utils/getFormattedValue';
import { getMemberDetails } from 'src/utils/getMemberDetails';

import { GlobalTaskData, LogItem, useTimesheetStore } from './store/useTimesheetStore';

const BILLABLE = 'Billable';
const NONBILLABLE = 'Non-Billable';

const getMemberName = (i: string) => {
  const member = getMemberDetails(i);

  if (member?.firstname) {
    return member.firstname;
  }

  return '';
};

const getMemberId = (i: string) => {
  const member = getMemberDetails(i);

  if (member?._id) {
    return member._id;
  }

  return '';
};

const durationToMilliseconds = (time: { hours: string; minutes: string; seconds: string }) => {
  const { hours = '0', minutes = '0', seconds = '0' } = time;
  const hoursInMs = Number(hours) * 60 * 60 * 1000;
  const minutesInMs = Number(minutes) * 60 * 1000;
  const secondsInMs = Number(seconds) * 1000;
  const result = hoursInMs + minutesInMs + secondsInMs;
  return result;
};

const getLoggedTimeInMS = (logs: GlobalTaskData['logs'] | LogItem[]) => {
  const timeDiff = logs.map((l) => {
    if (l.duration) return durationToMilliseconds(l.duration);
    else if (l.endTime) {
      const ms = differenceInMilliseconds(new Date(l.endTime), new Date(l.startTime));
      const differenceInSeconds = ms / 1000;

      const hours = Math.floor(differenceInSeconds / 3600);
      const minutes = Math.floor((differenceInSeconds % 3600) / 60);
      const seconds = Math.floor(differenceInSeconds % 60);
      const result = {
        hours: hours.toString(),
        minutes: minutes.toString(),
        seconds: seconds.toString(),
      };
      return durationToMilliseconds(result);
    } else return 0;
  });
  const totalMs = timeDiff.reduce((acc, curr) => acc + curr, 0);
  return totalMs;
};

export const getRowGroupedData = (filteredTimeLogs, groupedBy, filterNodeFunction) => {
  switch (groupedBy) {
    case 'tasks': {
      const allLogs = filteredTimeLogs
        .map((i) => [
          Object.assign({}, i, {
            path: [i._id],
            member: uniq(i.logs.map((l) => getMemberName(l.member))),
            billable: null,
            billingType: uniq(i.logs.map((l) => (l.billable ? BILLABLE : NONBILLABLE))),
            loggedAt: null,
            isLog: false,
            timePeriod: [],
            logs: i.logs.filter((log) =>
              filterNodeFunction({
                data: {
                  workspace: i?.workspace?.name || '',
                  file: i?.file?.title || '',
                  assignee: i?.assignee.map(getMemberName) || [],
                  tasks: getFormattedValue(i?.name),
                  parent: null,
                  path: [i._id],
                  member: getMemberName(log.member),
                  daily_log: getLoggedTimeInMS([log]),
                  billable: log.billable,
                  billingType: log.billable ? BILLABLE : NONBILLABLE,
                  loggedAt: new Date(log.startDate),
                  isLog: true,
                  timePeriod: log.startTime && log.endTime ? [log.startTime, log.endTime] : [],
                },
              })
            ),
          }),
          ...i.logs.map((l, index) =>
            Object.assign(
              {},
              {
                workspace: i.workspace,
                file: i.file,
                // name: `Log ${index + 1}`,
                name: i.name,
                assignee: i.assignee,
                member: getMemberName(l.member),
                parent: null,
                billable: l.billable,
                billingType: l.billable ? BILLABLE : NONBILLABLE,
                path: [i._id, l._id],
                _id: l._id,
                logs: [l],
                loggedAt: new Date(l.startDate),
                isLog: true,
                timePeriod: l.startTime && l.endTime ? [l.startTime, l.endTime] : [],
              }
            )
          ),
        ])
        .flat()
        .filter((i) => i.logs?.length > 0);

      const data = allLogs.map((log) => {
        return {
          tasks: getFormattedValue(log?.name),
          parent: log?.parent,
          path: log?.path,
          member: log?.member,
          billable: log?.billable,
          billingType: log?.billingType,
          daily_log: getLoggedTimeInMS(log?.logs),
          loggedTime: getLoggedTimeInMS(log?.logs),
          loggedAt: log?.loggedAt,
          workspace: log?.workspace?.name || '',
          file: log?.file?.title || '',
          assignee: log?.assignee.map(getMemberName) || [],
          isLog: log?.isLog,
          timePeriod: log?.timePeriod,
          // billing: 'Billable',
          // action: 'delete',
        };
      });

      return data;
    }
    case 'users': {
      const allLogs = filteredTimeLogs
        .map((i) =>
          i.logs.map((l) =>
            Object.assign(
              {},
              {
                workspace: i.workspace,
                file: i.file,
                name: i.name,
                assignee: i.assignee,
                memberName: getMemberName(l.member),
                memberId: getMemberId(l.member),
                parent: null,
                billable: l.billable,
                billingType: l.billable ? BILLABLE : NONBILLABLE,
                path: [getMemberId(l.member), l._id],
                _id: l._id,
                logs: [l].flat(),
                loggedAt: new Date(l.startDate),
                isLog: true,
                timePeriod: l.startTime && l.endTime ? [l.startTime, l.endTime] : [],
              }
            )
          )
        )
        .flat()
        .filter((log) => {
          const node = {
            data: {
              tasks: getFormattedValue(log?.name),
              parent: log?.parent,
              path: log?.path,
              member: log?.memberName,
              billable: log?.billable,
              billingType: log?.billingType,
              daily_log: getLoggedTimeInMS(log?.logs),
              loggedTime: getLoggedTimeInMS(log?.logs),
              loggedAt: log?.loggedAt,
              workspace: log?.workspace?.name || '',
              file: log?.file?.title || '',
              assignee: log?.assignee.map(getMemberName) || [],
              isLog: log?.isLog,
              timePeriod: log?.timePeriod,
              // billing: 'Billable',
              // action: 'delete',
            },
          };
          return filterNodeFunction(node);
        });

      const forUsers = allLogs.map((i) => ({
        workspace: i?.workspace?.name || '',
        file: i?.file?.title || '',
        name: i.memberName,
        assignee: i.assignee.map(getMemberName) || [],
        memberName: i.memberName,
        memberId: i.memberId,
        parent: i.memberId,
        billable: i.billable,
        billingType: i.billingType,
        _id: i._id,
        logs: i.logs,
        loggedAt: i.loggedAt,
        isLog: false,
        timePeriod: i.timePeriod,
      }));

      const groupedData = forUsers.reduce((a, c) => {
        if (!a[c.memberId]) {
          a[c.memberId] = Object.assign({}, c, {
            workspace: [c.workspace],
            file: [c.file],
            timePeriod: [...c.timePeriod],
            billingType: [c.billingType],
            path: [c.memberId],
            parent: null,
          });
        } else {
          a[c.memberId] = {
            workspace: uniq([...a[c.memberId].workspace, c.workspace]),
            file: uniq([...a[c.memberId].file, c.file]),
            name: c.memberName,
            assignee: [...a[c.memberId].assignee, ...c.assignee],
            memberName: c.memberName,
            memberId: c.memberId,
            parent: null,
            billable: c.billable,
            billingType: uniq([...a[c.memberId].billingType, c.billingType]),
            path: [c.memberId],
            _id: c._id,
            logs: [...a[c.memberId].logs, ...c.logs],
            loggedAt: null,
            isLog: false,
            // timePeriod: [...a[c.memberId].timePeriod, ...c.timePeriod].sort(
            //   (a, b) => new Date(a).getTime() - new Date(b).getTime()
            // ),
            timePeriod: [],
          };
        }
        return a;
      }, {});

      const userData = Object.values(groupedData).map((log: any) => {
        return {
          tasks: `${log?.name}'s logs`,
          parent: log?.parent,
          path: log?.path,
          member: log?.memberName,
          billable: null,
          billingType: log?.billingType,
          daily_log: getLoggedTimeInMS(log?.logs),
          loggedTime: getLoggedTimeInMS(log?.logs),
          loggedAt: log?.loggedAt,
          workspace: log?.workspace.filter((i) => i) || [],
          file: log?.file || [],
          assignee: uniq(log?.assignee.filter((i) => i)) || [],
          isLog: false,
          timePeriod: [],
          // timePeriod:
          //   log?.timePeriod.length > 1
          //     ? [log?.timePeriod[0], log?.timePeriod[log?.timePeriod.length - 1]]
          //     : [],
        };
      });

      const data = allLogs
        .filter((i) => i.isLog)
        .map((log) => {
          return {
            tasks: getFormattedValue(log?.name),
            parent: log?.parent,
            path: log?.path,
            member: log?.memberName,
            billable: log?.billable,
            billingType: log?.billingType,
            daily_log: getLoggedTimeInMS(log?.logs),
            loggedTime: getLoggedTimeInMS(log?.logs),
            loggedAt: log?.loggedAt,
            workspace: log?.workspace?.name || '',
            file: log?.file?.title || '',
            assignee: log?.assignee.map(getMemberName) || [],
            isLog: log?.isLog,
            timePeriod: log?.timePeriod,
            // billing: 'Billable',
            // action: 'delete',
          };
        });

      return [...data, ...userData];
    }
    default: {
      const allLogs = filteredTimeLogs
        .map((i) => [
          Object.assign({}, i, {
            path: [i._id],
            member: uniq(i.logs.map((l) => getMemberName(l.member))),
            billable: null,
            billingType: uniq(i.logs.map((l) => (l.billable ? BILLABLE : NONBILLABLE))),
            loggedAt: null,
            isLog: false,
            timePeriod: [],
          }),
          ...i.logs.map((l, index) =>
            Object.assign(
              {},
              {
                workspace: i.workspace,
                file: i.file,
                // name: `Log ${index + 1}`,
                name: i.name,
                assignee: i.assignee,
                member: getMemberName(l.member),
                parent: null,
                billable: l.billable,
                billingType: l.billable ? BILLABLE : NONBILLABLE,
                // path: [i._id, l._id],
                path: [l._id],
                _id: l._id,
                logs: [l],
                loggedAt: new Date(l.startDate),
                isLog: true,
                timePeriod: l.startTime && l.endTime ? [l.startTime, l.endTime] : [],
              }
            )
          ),
        ])
        .flat()
        .filter((i) => i.logs?.length > 0);

      const data = allLogs
        .filter((i) => i.isLog)
        .map((log) => {
          return {
            tasks: getFormattedValue(log?.name),
            parent: log?.parent,
            path: log?.path,
            member: log?.member,
            billable: log?.billable,
            billingType: log?.billingType,
            daily_log: getLoggedTimeInMS(log?.logs),
            loggedTime: getLoggedTimeInMS(log?.logs),
            loggedAt: log?.loggedAt,
            workspace: log?.workspace?.name || '',
            file: log?.file?.title || '',
            assignee: log?.assignee.map(getMemberName) || [],
            isLog: log?.isLog,
            timePeriod: log?.timePeriod,
            // billing: 'Billable',
            // action: 'delete',
          };
        });

      return data;
    }
  }
};

export const timePeriodFormatter = (dates: [string, string]) => {
  const timeDiff = differenceInDays(new Date(dates[1]), new Date(dates[0]));
  const ifMultipleDays = timeDiff > 0;
  const timeString = `${format(new Date(dates[0]), 'hh:mm a')} - ${format(
    new Date(dates[1]),
    'hh:mm a'
  )} ${ifMultipleDays ? `(+${timeDiff}d)` : ''}`;
  return timeString;
};
